import { useEffect, useRef, useState } from "react";
import { useTheme } from "styled-components";
import { TitleBar, IconButton, FloatingWrap, FloatingBody } from "./components/DockablePanelStyles";

const STORAGE_PREFIX = "pinmonkey_editor_panel_";
const SNAP_PX = 48; // distance from the dock edge that triggers a snap-back
const MIN_VISIBLE = 40; // px of the panel that must stay on-screen while floating
const BASE_Z = 60;

// Shared across every floating panel instance (module singleton) — clicking
// a panel's title bar bumps it above whichever panel was previously on top.
let topZ = BASE_Z;

const loadState = (id) => {
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${id}`);
    if (!raw) return { floating: false, x: 80, y: 80 };
    const parsed = JSON.parse(raw);
    return {
      floating: !!parsed.floating,
      x: typeof parsed.x === "number" ? parsed.x : 80,
      y: typeof parsed.y === "number" ? parsed.y : 80,
    };
  } catch {
    return { floating: false, x: 80, y: 80 };
  }
};

// Wraps a docked panel with a drag handle so it can be popped into a
// freely-draggable floating window, and snapped back to its docked slot
// (its `edge` — 'right' or 'bottom') by dragging it near that edge again.
// Floating/dock state + position persists per `id` in localStorage.
export const DockablePanel = ({ id, title, edge, children }) => {
  const { colors } = useTheme();
  const border = colors("highlight", 0.3);
  const chrome = colors("chrome");
  const text = colors("highlight", 0.7);
  const bg = colors("background");
  const accent = colors("accent");

  const [state, setState] = useState(() => loadState(id));
  const [nearEdge, setNearEdge] = useState(false);
  const [zIndex, setZIndex] = useState(BASE_Z);
  const wrapRef = useRef(null);
  const dragRef = useRef(null); // { startX, startY, originX, originY } while dragging

  const bringToFront = () => setZIndex(++topZ);

  useEffect(() => {
    try {
      window.localStorage.setItem(`${STORAGE_PREFIX}${id}`, JSON.stringify(state));
    } catch {
      // ignore (private mode / storage disabled)
    }
  }, [id, state]);

  const isNearEdge = (rect) =>
    edge === "right"
      ? window.innerWidth - rect.right < SNAP_PX
      : window.innerHeight - rect.bottom < SNAP_PX;

  const clamp = (x, y, w, h) => ({
    x: Math.min(Math.max(x, MIN_VISIBLE - w), window.innerWidth - MIN_VISIBLE),
    y: Math.min(Math.max(y, 0), window.innerHeight - MIN_VISIBLE),
  });

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const w = wrapRef.current?.offsetWidth ?? 0;
    const h = wrapRef.current?.offsetHeight ?? 0;
    const { x, y } = clamp(d.originX + (e.clientX - d.startX), d.originY + (e.clientY - d.startY), w, h);
    setState((prev) => ({ ...prev, floating: true, x, y }));
    if (wrapRef.current) setNearEdge(isNearEdge(wrapRef.current.getBoundingClientRect()));
  };

  const endDrag = () => {
    if (!dragRef.current) return;
    dragRef.current = null;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", endDrag);
    const rect = wrapRef.current?.getBoundingClientRect();
    if (rect && isNearEdge(rect)) setState((prev) => ({ ...prev, floating: false }));
    setNearEdge(false);
  };

  const startDrag = (e) => {
    e.preventDefault();
    bringToFront();
    let originX = state.x;
    let originY = state.y;
    if (!state.floating) {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (rect) {
        originX = rect.left;
        originY = rect.top;
      }
      setState((prev) => ({ ...prev, floating: true, x: originX, y: originY }));
    }
    dragRef.current = { startX: e.clientX, startY: e.clientY, originX, originY };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endDrag);
  };

  const toggleFloat = () => {
    bringToFront();
    if (state.floating) {
      setState((prev) => ({ ...prev, floating: false }));
      return;
    }
    const rect = wrapRef.current?.getBoundingClientRect();
    setState((prev) => ({
      ...prev,
      floating: true,
      x: rect ? rect.left : prev.x,
      y: rect ? rect.top : prev.y,
    }));
  };

  // Drop any in-flight drag listeners if the panel unmounts mid-drag
  useEffect(
    () => () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", endDrag);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const bar = (
    <TitleBar
      style={{
        color: text,
        background: chrome,
        border: `1px solid ${nearEdge ? accent : border}`,
        borderBottom: state.floating ? `1px solid ${nearEdge ? accent : border}` : "none",
      }}
      onPointerDown={startDrag}
    >
      <span style={{ opacity: 0.5 }}>⠿⠿</span>
      <span>{title}</span>
      <IconButton
        type="button"
        title={state.floating ? "Dock" : "Float"}
        onClick={toggleFloat}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {state.floating ? "⇲" : "⇱"}
      </IconButton>
    </TitleBar>
  );

  if (!state.floating) {
    return (
      <div ref={wrapRef}>
        {bar}
        {children}
      </div>
    );
  }

  return (
    <FloatingWrap ref={wrapRef} style={{ left: state.x, top: state.y, zIndex }}>
      {bar}
      <FloatingBody style={{ border: `1px solid ${border}`, borderTop: "none", background: bg }}>
        {children}
      </FloatingBody>
    </FloatingWrap>
  );
};
